import './fonts.css';

import { LicenseInfo } from '@mui/x-license-pro';

import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import _ from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';

import sentryService from './providers/sentry';

import * as serviceWorker from './serviceWorker';
import { logoutSuccess } from './actions/logout';
import axiosInstance from './core/http';
import configureStore from './store/configureStore';
import mixpanelService from './providers/mixpanel';

import { clearAppSessionStorage, isMosAuthProvider } from './commons/auth/session';
import {
  clearMosLocalSession,
  hasStoredMosOidcUser,
  shouldRetryFailedMosRequest,
  startMosTokenRenewal,
  tryRenewMosAccessToken,
} from './services/mosAuth';
import { getAuthorizationHeader } from './services/authHeaders';
import App from './routes/App';
import MuiTheme from './mui-theme';
import theme from './theme';

const preloadedState = {
  baseReducer: {
    activeStores: [],
    brands: [],
    currency: {},
    diffChange: [],
    events: [],
    eventSubCategories: [],
    featuresKeyByPath: {},
    forecasts: [],
    isCashierProductSalesIngestionPipelineRunning: false,
    isCentralView: false,
    isConnectionSlow: false,
    isSynchronizingCashierProducts: false,
    isSynchronizingCashierProductsSales: false,
    isUserOffline: false,
    lastYearSales: [],
    locations: [],
    lyEvents: [],
    lyWeather: [],
    pastEvents: [],
    pastForecasts: [],
    pastSales: [],
    pastWeather: [],
    productionMetricsByDates: {},
    products: [],
    rangeForecasts: [],
    rangeSales: [],
    retailers: [],
    sales: [],
    shouldReloadApp: false,
    store: {},
    stores: [],
    supplierProductsCount: 0,
    suppliers: [],
    suppliersCount: 0,
    user: {},
    userRights: [],
    weather: [],
    weatherStation: [],
    weeklyForecasts: [],
    weeklySales: [],
  },
  confirmationMessageReducer: {
    message: '',
    messageBool: false,
    messageType: 'success',
  },
  form: {},
  loadingReducer: {},
  modalReducer: {},
  orderConfirmationMessageReducer: {},
  stockFormReducer: {
    totByStorageAreaIdAndRecipeId: {},
    totByStorageAreaIdSPIdAndPackagingId: {},
  },
  switchButtonReducer: {
    supplierProductId: '',
  },
};

const store = configureStore(preloadedState);

axiosInstance.interceptors.request.use(
  async (config) => {
    config.metadata = { startTime: new Date() };

    if (!config.headers) {
      config.headers = {};
    }

    // Legacy services still set Authorization from loopbackAccessToken. On MOS sessions
    // that value is missing — replace with the session-aware header so those calls work.
    // Explicit SSO / invite / reset tokens are preserved when there is no MOS session.
    const existingAuthorization = config.headers.Authorization || config.headers.authorization;
    const authorization = await getAuthorizationHeader(existingAuthorization);

    if (authorization) {
      config.headers.Authorization = authorization;
    } else {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }

    return config;
  },
  (error) => {
    error.config.metadata.endTime = new Date();
    error.duration = error.config.metadata.endTime - error.config.metadata.startTime;

    return Promise.reject(error);
  },
);

const isUnauthorizedResponse = (error) => {
  const hasUnauthorizedStatus =
    _.get(error, 'response.data.error.status') === 401 ||
    _.get(error, 'response.data.error.statusCode') === 401 ||
    _.get(error, 'response.status') === 401 ||
    _.get(error, 'response.data.status') === 401 ||
    _.get(error, 'response.data.statusCode') === 401;

  if (!hasUnauthorizedStatus) {
    return false;
  }

  /**
   * NOTE: NOT PRETTY BUT WE ARE STILL TRYNG TO UNDERSTAND WHY RESPONSE
   * IS DIFFERENT FROM DEV TO PRODUCTION MODE
   */
  const unauthorizedMessages = ['Authorization Required', 'Unauthorized'];

  return (
    unauthorizedMessages.includes(_.get(error, 'response.data.error.message')) ||
    unauthorizedMessages.includes(_.get(error, 'response.data.message')) ||
    unauthorizedMessages.includes(_.get(error, 'response.statusText'))
  );
};

const clearSessionAfterUnauthorized = () => {
  store.dispatch(logoutSuccess());

  // MOS 401: clear local session only (no immediate end-session).
  // Next "Login with MOS" forces end-session first so SSO does not silent-reauth.
  // Also clear when OIDC storage exists but authProvider was never set (bootstrap 401).
  if (isMosAuthProvider() || hasStoredMosOidcUser()) {
    clearMosLocalSession();
  } else {
    clearAppSessionStorage();
  }
};

axiosInstance.interceptors.response.use(
  (response) => {
    response.config.metadata.endTime = new Date();
    response.duration = response.config.metadata.endTime - response.config.metadata.startTime;

    return response;
  },
  async (error) => {
    if (!isUnauthorizedResponse(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const sentAuthorization =
      originalRequest?.headers?.Authorization || originalRequest?.headers?.authorization;

    const shouldRetry = shouldRetryFailedMosRequest({
      alreadyRetried: Boolean(originalRequest?._mosRefreshRetry),
      sentAuthorization,
    });

    if (!shouldRetry || !originalRequest) {
      clearSessionAfterUnauthorized();

      return Promise.reject(error);
    }

    originalRequest._mosRefreshRetry = true;

    const renewedAccessToken = await tryRenewMosAccessToken();

    if (!renewedAccessToken) {
      clearSessionAfterUnauthorized();

      return Promise.reject(error);
    }

    if (!originalRequest.headers) {
      originalRequest.headers = {};
    }

    originalRequest.headers.Authorization = `Bearer ${renewedAccessToken}`;

    return axiosInstance(originalRequest);
  },
);

startMosTokenRenewal();

// Init sentry instance
sentryService.init();

/**
 * As recommended by the MUI docs: https://mui.com/x/introduction/licensing/#how-to-install-the-key
 * The issue of leaving the license key hardcoded is a known subject, the MUI team has been
 * reflecting on it since 2021: https://github.com/mui/mui-x/issues/2565
 */
// To avoid crashing the app in development mode, we set the license key to UNLICENSED
if (process.env.NODE_ENV === 'development' || !process.env.NODE_ENV) {
  LicenseInfo.setLicenseKey('UNLICENSED');
} else {
  LicenseInfo.setLicenseKey(
    '95f97d661ff9c866a07f84ec43d305c9Tz04Njc1MSxFPTE3NDI0NTk1NzQwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=',
  );
}

ReactDOM.render(
  <Provider store={store}>
    <MuiThemeProvider theme={MuiTheme}>
      <ThemeProvider theme={theme}>
        <App />
      </ThemeProvider>
    </MuiThemeProvider>
  </Provider>,
  document.getElementById('root'),
);

/**
 * Connecting app to mix panel project
 */
if (process.env.REACT_APP_MIX_PANEL_PROJECT_ID) {
  const mixPanel = mixpanelService.getInstance();

  mixPanel.init(process.env.REACT_APP_MIX_PANEL_PROJECT_ID);
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
// serviceWorker.register({
//   onUpdate: registration => {
//     const waitingServiceWorker = registration.waiting

//     if (waitingServiceWorker) {
//       waitingServiceWorker.postMessage({ type: "SKIP_WAITING" });
//     }
//   }
// });
